if (!customElements.get('shopsta-product-gallery')) {
  customElements.define('shopsta-product-gallery', class extends HTMLElement {
    connectedCallback() {
      this.scheduleSelected = this.scheduleSelected.bind(this);
      this.onImageLoad = this.onImageLoad.bind(this);
      this.addEventListener('load', this.onImageLoad, true);
      this.observer = new MutationObserver(this.scheduleSelected);
      this.observer.observe(this, {
        subtree: true,
        attributes: true,
        attributeFilter: ['data-gallery-selected'],
      });
      this.visible = false;
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.visible = entries.some(entry => entry.isIntersecting);
        if (this.visible) this.scheduleSelected();
      }, { rootMargin: '200px 0px' });
      this.intersectionObserver.observe(this);
      this.resizeObserver = new ResizeObserver(() => {
        this.resizeImages = true;
        this.scheduleSelected();
      });
      this.resizeObserver.observe(this);
      // Native lazy loading fetches a long horizontal thumbnail strip at once.
      // Observe the strip's clipped area so those downloads do not compete with
      // the main image. The selected thumbnail is always available immediately.
      const thumbnails = this.querySelectorAll('[data-gallery-thumbnail-src]');
      this.thumbnailObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          this.thumbnailObserver.unobserve(entry.target);
          this.loadThumbnail(entry.target);
        });
      }, { root: this.querySelector('[data-gallery-scroller]'), rootMargin: '75px' });
      thumbnails.forEach(image => this.thumbnailObserver.observe(image));
      this.scheduleSelected();
    }

    disconnectedCallback() {
      this.removeEventListener('load', this.onImageLoad, true);
      this.observer.disconnect();
      this.intersectionObserver.disconnect();
      this.resizeObserver.disconnect();
      this.thumbnailObserver.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    loadThumbnail(image) {
      if (!image?.dataset.galleryThumbnailSrc || image.shopstaThumbnailPending) return;
      image.shopstaThumbnailPending = true;
      const activate = () => {
        image.shopstaThumbnailPending = false;
        if (!image.isConnected || !image.dataset.galleryThumbnailSrc) return;
        image.srcset = image.dataset.galleryThumbnailSrcset || '';
        image.src = image.dataset.galleryThumbnailSrc;
        delete image.dataset.galleryThumbnailSrc;
        delete image.dataset.galleryThumbnailSrcset;
      };
      if (typeof window.ShopstaLocalizeGalleryThumbnail === 'function') {
        window.ShopstaLocalizeGalleryThumbnail(image, activate);
      } else activate();
    }

    loadImage(figure, priority) {
      if (!figure) return;
      const image = figure.querySelector('[data-gallery-image]');
      if (!image) return;
      image.fetchPriority = priority;

      const activateDeferredImage = () => {
        if (!image.isConnected) return;
        if (image.dataset.gallerySrc) {
          image.srcset = image.dataset.gallerySrcset || '';
          image.src = image.dataset.gallerySrc;
          delete image.dataset.gallerySrc;
          delete image.dataset.gallerySrcset;
        }
        const background = figure.querySelector('[data-gallery-background]');
        if (background) {
          background.style.backgroundImage = `url("${background.dataset.galleryBackground}")`;
          delete background.dataset.galleryBackground;
        }
      };

      if (figure.dataset.galleryIndex !== '0' && !figure.shopstaLocaleReady && typeof window.ShopstaLocalizeGalleryFigure === 'function') {
        if (figure.shopstaLocalizationPending) return;
        figure.shopstaLocalizationPending = true;
        window.ShopstaLocalizeGalleryFigure(figure, () => {
          figure.shopstaLocalizationPending = false;
          figure.shopstaLocaleReady = true;
          activateDeferredImage();
        });
        return;
      }

      activateDeferredImage();
    }

    imageSize(image) {
      const figure = image.closest('[data-gallery-figure]');
      const bounds = figure.getBoundingClientRect();
      let width = bounds.width;
      if (this.dataset.productGalleryAspectRatio !== 'natural' && !this.hasAttribute('data-product-gallery-image-crop')) {
        const ratio = Number(image.getAttribute('width')) / Number(image.getAttribute('height'));
        if (bounds.height > 0 && ratio > 0) width = Math.min(width, bounds.height * ratio);
      }
      return width > 0 ? `${Math.ceil(width)}px` : image.sizes;
    }

    scheduleSelected() {
      // Selection, image-load and resize observers can all fire in one frame.
      // Coalesce them, then read every required size before changing the DOM.
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.loadSelected();
      });
    }

    loadSelected() {
      if (!this.isConnected) return;
      const figures = Array.from(this.querySelectorAll('[data-gallery-figure]'));
      const selected = figures.findIndex(figure => figure.dataset.gallerySelected === 'true');
      if (selected < 0) return;
      const requests = [{ figure: figures[selected], priority: 'high' }];
      const image = figures[selected].querySelector('[data-gallery-image]');
      if (this.visible && (!image || (!image.dataset.gallerySrc && image.complete && image.naturalWidth > 0))) {
        [selected - 1, selected + 1].forEach(index => {
          const neighbor = (index + figures.length) % figures.length;
          if (neighbor !== selected && !requests.some(request => request.figure === figures[neighbor])) {
            requests.push({ figure: figures[neighbor], priority: 'low' });
          }
        });
      }
      const images = new Set(requests.map(request => request.figure.querySelector('[data-gallery-image]')).filter(Boolean));
      if (this.resizeImages) {
        this.querySelectorAll('[data-gallery-image]:not([data-gallery-src])').forEach(loaded => images.add(loaded));
        this.resizeImages = false;
      }
      const sizes = Array.from(images, loaded => [loaded, this.imageSize(loaded)]);
      sizes.forEach(([loaded, size]) => { if (loaded.sizes !== size) loaded.sizes = size; });
      requests.forEach(request => this.loadImage(request.figure, request.priority));
      const selectedThumbnail = this.querySelector('[data-gallery-thumbnail][data-gallery-selected="true"] [data-gallery-thumbnail-src]');
      if (selectedThumbnail) this.loadThumbnail(selectedThumbnail);
    }

    onImageLoad(event) {
      if (!event.target.matches('[data-gallery-image]') || event.target.dataset.gallerySrc) return;
      event.target.dispatchEvent(new CustomEvent('rimg:load', { bubbles: true }));
      this.scheduleSelected();
    }
  });
}
